/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import { useCallback, useEffect, useState } from 'react';
import { isBefore, startOfDay } from 'date-fns';
import { DateChangeEvent } from 'carbon-react/lib/components/date';

import { ReportScheduleItem } from '@futrli-sdk/report_scheduling';
import { WorkspaceMember } from '@futrli-sdk/people';

import { forceLocalDate } from '../../../../utils/dates';
import { RecurrenceEndType, RecurrenceWeekDay, THE_LAST } from '../../../../ui/recurrence-settings/types';

export enum Recurrence {
  DOES_NOT_REPEAT = 'do-not-repeat',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  ANNUALLY = 'annually'
}

export { RecurrenceEndType, RecurrenceWeekDay };

const REPEATING_FREQUENCY_QUARTERLY = 3;
const REPEATING_FREQUENCY_ANNUALLY = 12;

interface UseRecurrenceStateParams {
  initialSchedule?: ReportScheduleItem;
  members: WorkspaceMember[];
}

/**
 * Manages all recurrence-related state for the schedule report form.
 * Handles frequency selection, start/end dates, repeat intervals, weekday/month-day picks,
 * and auto-populates values from an existing schedule when editing.
 */
export const useRecurrenceState = ({ initialSchedule, members }: UseRecurrenceStateParams) => {
  const [recurrence, setRecurrence] = useState(Recurrence.DOES_NOT_REPEAT);
  const [recurrenceStartDate, setRecurrenceStartDate] = useState(new Date());
  const [recurrenceStartDateError, setRecurrenceStartDateError] = useState(false);
  const [recurrenceRepeatValue, setRecurrenceRepeatValue] = useState('1');
  const [recurrenceEndType, setRecurrenceEndType] = useState(RecurrenceEndType.NEVER);
  const [occurancesValue, setOccurancesValue] = useState('10');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(new Date());
  const [recurrenceEndDateIsBeforeStartDate, setRecurrenceEndDateIsBeforeStartDate] = useState(false);
  const [recurrenceWeekDay, setRecurrenceWeekDay] = useState(RecurrenceWeekDay.MONDAY);
  const [recurrenceMonthDay, setRecurrenceMonthDay] = useState(THE_LAST);

  // Set repeat value for quarterly/annually
  useEffect(() => {
    if (recurrence === Recurrence.QUARTERLY) {
      setRecurrenceRepeatValue(String(REPEATING_FREQUENCY_QUARTERLY));
    } else if (recurrence === Recurrence.ANNUALLY) {
      setRecurrenceRepeatValue(String(REPEATING_FREQUENCY_ANNUALLY));
    }
  }, [recurrence]);

  // Populate from initial schedule (edit mode)
  useEffect(() => {
    if (initialSchedule && members.length > 0) {
      if (
        initialSchedule.period === 'monthly' &&
        initialSchedule.repeating_frequency === REPEATING_FREQUENCY_QUARTERLY
      ) {
        setRecurrence(Recurrence.QUARTERLY);
      } else if (
        initialSchedule.period === 'monthly' &&
        initialSchedule.repeating_frequency === REPEATING_FREQUENCY_ANNUALLY
      ) {
        setRecurrence(Recurrence.ANNUALLY);
      } else {
        setRecurrence(initialSchedule.period as Recurrence);
      }

      setRecurrenceStartDate(forceLocalDate(initialSchedule.start_date));
      setRecurrenceRepeatValue(String(initialSchedule.repeating_frequency));
      setRecurrenceEndType(initialSchedule.end_type as RecurrenceEndType);
      setOccurancesValue(
        typeof initialSchedule.end_after_occurrences === 'number' ? String(initialSchedule.end_after_occurrences) : '10'
      );
      if (initialSchedule.end_date) {
        setRecurrenceEndDate(forceLocalDate(initialSchedule.end_date));
      }
      setRecurrenceWeekDay(
        initialSchedule.weekday ? (initialSchedule.weekday as RecurrenceWeekDay) : RecurrenceWeekDay.MONDAY
      );
      setRecurrenceMonthDay(
        initialSchedule.day_of_month
          ? initialSchedule.day_of_month === 31
            ? THE_LAST
            : String(initialSchedule.day_of_month)
          : THE_LAST
      );

      const isStartInPast = isBefore(startOfDay(forceLocalDate(initialSchedule.start_date)), startOfDay(new Date()));
      setRecurrenceStartDateError(isStartInPast);
    }
  }, [initialSchedule, members]);

  // Validate end date is not before start date
  useEffect(() => {
    if (recurrenceEndType === RecurrenceEndType.ON_THIS_DAY) {
      setRecurrenceEndDateIsBeforeStartDate(isBefore(startOfDay(recurrenceEndDate), startOfDay(recurrenceStartDate)));
    } else {
      setRecurrenceEndDateIsBeforeStartDate(false);
    }
  }, [recurrenceEndDate, recurrenceStartDate, recurrenceEndType]);

  // Event handlers
  const handleStartDateChange = useCallback((e: DateChangeEvent) => {
    if (e.target.value.rawValue) {
      const date = new Date(e.target.value.rawValue);
      setRecurrenceStartDate(date);
      setRecurrenceStartDateError(isBefore(startOfDay(date), startOfDay(new Date())));
    }
  }, []);

  const handleRepeatValueChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRecurrenceRepeatValue(event.target.value);
  }, []);

  const handleEndTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRecurrenceEndType(event.target.value as RecurrenceEndType);
  }, []);

  const handleOccurancesValueChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setOccurancesValue(event.target.value);
  }, []);

  const handleEndDateChange = useCallback((e: DateChangeEvent) => {
    if (e.target.value.rawValue) {
      setRecurrenceEndDate(new Date(e.target.value.rawValue));
    }
  }, []);

  const handleWeekDayChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRecurrenceWeekDay(event.target.value as RecurrenceWeekDay);
  }, []);

  const handleMonthDayChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRecurrenceMonthDay(event.target.value);
  }, []);

  return {
    recurrence,
    setRecurrence,
    recurrenceStartDate,
    recurrenceStartDateError,
    recurrenceRepeatValue,
    recurrenceEndType,
    occurancesValue,
    recurrenceEndDate,
    recurrenceEndDateIsBeforeStartDate,
    recurrenceWeekDay,
    recurrenceMonthDay,
    handleStartDateChange,
    handleRepeatValueChange,
    handleEndTypeChange,
    handleOccurancesValueChange,
    handleEndDateChange,
    handleWeekDayChange,
    handleMonthDayChange,
    showRecurrenceSettings: recurrence !== Recurrence.DOES_NOT_REPEAT
  };
};

export type RecurrenceState = ReturnType<typeof useRecurrenceState>;
